// src/screens/PlanTraining.tsx
// ✅ PlanTraining avec i18n + réordonnancement + modèles + Bloc Tir (jsonb teams.players)
import { useEffect, useState, useMemo, useRef, useCallback, useLayoutEffect } from "react";
import { supabase } from "../supabaseClient";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { TrainingDrawingCanvas } from "../components/TrainingDrawingCanvas";
import ShootingBlock from "../components/ShootingBlock";
import { BackButton } from "../components/BackButton";
import {
  NotebookPen,
  PlusCircle,
  Save,
  Trash2,
  Plus,
  Maximize2,
  X,
  Loader2,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  GripVertical,
  BookmarkPlus,
  Library,
  Trash,
  Search,
  Filter,
} from "lucide-react";
import i18n from "../i18n";

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);

  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null
        ? String((i18n.t as any)(key, { ...options, lng }))
        : String((i18n.t as any)(key, { lng })),
    [lng]
  );

  return { t, lng };
}

/* ===== Types ===== */
type BlockKind = "echauffement" | "exercice" | "prepa" | "shooting";

type ExerciseBlock = {
  id: string;
  kind: BlockKind;
  name: string;
  title?: string;
  duration: string;
  objectives: string[];
  description: string;
  technicalNotes: string;
  evolutions: string;
  drawings: DrawingV2[];
};

type Training = { id: string; date: string; team_id: string | null };
type TrainingPlanRow = { id: string; training_id: string; exercises: ExerciseBlock[] };

type BlockTemplateRow = {
  id: string;
  user_id: string;
  name: string;
  kind: BlockKind;
  payload: ExerciseBlock;
  created_at: string;
};

type DrawElementKind = "arrow" | "cone" | "player" | "text" | "pass";

type DrawElement = {
  id: string;
  kind: DrawElementKind;
  x: number; y: number;
  x2?: number; y2?: number;
  label?: string;
  color?: string;
  cpx?: number; cpy?: number;
  hasBall?: boolean;
};

type NormPoint = { x: number; y: number };
type DrawingV2 = {
  id: string;
  paths: { x: number; y: number }[][];
  type: "half" | "full";
  elements?: DrawElement[];
  balls?: NormPoint[];
};
function normalizeLegacyDrawing(d: DrawingV2): DrawingV2 {
  if (!d) return d;

  const paths = Array.isArray(d.paths) ? d.paths : [];
  const elements = Array.isArray(d.elements) ? d.elements : [];

  const hasBigCoordInPaths = paths.some(seg =>
    seg.some(pt => pt.x > 1.5 || pt.y > 1.5)
  );
  const hasBigCoordInElems = elements.some(el =>
    el.x > 1.5 ||
    el.y > 1.5 ||
    (el.x2 ?? 0) > 1.5 ||
    (el.y2 ?? 0) > 1.5 ||
    (el.cpx ?? 0) > 1.5 ||
    (el.cpy ?? 0) > 1.5
  );

  if (!hasBigCoordInPaths && !hasBigCoordInElems) {
    // déjà en 0..1
    return d;
  }

  const BASE_W = 520;
  const BASE_H_FULL = 520;
  const BASE_H_HALF = 520 * 1.3;
  const baseH = d.type === "half" ? BASE_H_HALF : BASE_H_FULL;

  const norm = (v: number, max: number) => v / max;

  const normPaths = hasBigCoordInPaths
    ? paths.map(seg =>
      seg.map(pt => ({
        x: norm(pt.x, BASE_W),
        y: norm(pt.y, baseH),
      }))
    )
    : paths;

  const normElems = hasBigCoordInElems
    ? elements.map(el => ({
      ...el,
      x: norm(el.x, BASE_W),
      y: norm(el.y, baseH),
      ...(el.x2 != null ? { x2: norm(el.x2, BASE_W) } : {}),
      ...(el.y2 != null ? { y2: norm(el.y2, baseH) } : {}),
      ...(el.cpx != null ? { cpx: norm(el.cpx, BASE_W) } : {}),
      ...(el.cpy != null ? { cpy: norm(el.cpy, baseH) } : {}),
    }))
    : elements;

  return {
    ...d,
    paths: normPaths,
    elements: normElems,
  };
}


type Tool = "free" | "arrow" | "cone" | "player" | "text" | "pen" | "pass" | "ball";

/** 🧑‍🤝‍🧑 Liste minimale de joueurs pour le bloc Tir */
type PlayerLite = { firstName: string; lastName: string; number: number };

export const PlanTraining = () => {
  const { t } = useI18nSafe();
  const [exercises, setExercises] = useState<ExerciseBlock[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [selectedTrainingId, setSelectedTrainingId] = useState<string>("");
  const [planId, setPlanId] = useState<string | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation() as { state?: { trainingId?: string } };
  const [tool, setTool] = useState<Tool>("free");
  const [toolColor, setToolColor] = useState<string>("#111827");
  const near = (ax: number, ay: number, bx: number, by: number, thr = 0.04) =>
    Math.hypot(ax - bx, ay - by) <= thr;

  // ➕ popup d’édition
  const [editing, setEditing] = useState<null | { exerciseId: string; drawingId: string }>(null);

  // 🔀 drag & drop
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // 📚 Modales / états pour modèles
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState<BlockTemplateRow[]>([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplQuery, setTplQuery] = useState("");
  const [tplKind, setTplKind] = useState<"all" | BlockKind>("all");

  // 🧑‍🤝‍🧑 État joueurs (pour le Bloc Tir)
  const [teamPlayers, setTeamPlayers] = useState<PlayerLite[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);


  const kindLabel = useCallback(
    (k: BlockKind) =>
      k === "echauffement"
        ? t("planTraining:kinds.echauffement")
        : k === "exercice"
          ? t("planTraining:kinds.exercice")
          : k === "prepa"
            ? t("planTraining:kinds.prepa")
            : t("planTraining:kinds.shooting"),
    [t]
  );
  const isDefaultShootingName = (s?: string) => {
    if (!s) return false;
    const canon = [
      "Séance de tir",
      "Bloc de tirs",
      "Shooting block",
      // au cas où l’i18n change :
      (i18n.t as any)("planTraining:kinds.shooting", { lng: i18n.language || "fr" }),
    ].filter(Boolean) as string[];
    const val = s.trim().toLowerCase();
    return canon.some(c => c.trim().toLowerCase() === val);
  };

  // Normalise un bloc existant (rétro-compat + shooting sans dessin imposé)
  const normalizeBlock = useCallback((ex: any, idx: number): ExerciseBlock => {
    const sanitizeElements = (els: any[] | undefined): DrawElement[] =>
      Array.isArray(els) ? (els.filter(e => e && e.kind !== "defender") as DrawElement[]) : [];

    const kind: BlockKind = (ex.kind as BlockKind) ?? (idx === 0 ? "echauffement" : "exercice");

    return {
      id: ex.id ?? uuidv4(),
      kind,
      name: kind === "shooting" && isDefaultShootingName(ex.name) ? "" : (ex.name ?? ""),
      title: ex.title ?? (kind === "shooting"
        ? t("planTraining:kinds.shooting")
        : idx === 0 ? t("planTraining:kinds.echauffement") : `${t("planTraining:kinds.exercice")} ${idx}`),
      duration: ex.duration ?? "",
      objectives: Array.isArray(ex.objectives) ? ex.objectives : [""],
      description: ex.description ?? "",
      technicalNotes: ex.technicalNotes ?? "",
      evolutions: ex.evolutions ?? "",
      drawings:
        kind === "shooting"
          ? (Array.isArray(ex.drawings) ? ex.drawings : [])
          : (Array.isArray(ex.drawings) && ex.drawings.length
            ? ex.drawings.map((d: any) => ({
              id: d.id ?? uuidv4(),
              paths: d.paths ?? [],
              type: (d.type as "half" | "full") ?? "half",
              elements: sanitizeElements(d.elements),
              balls: Array.isArray(d.balls) ? d.balls : [],
            }))
            : [{
              id: uuidv4(),
              paths: [],
              type: "half",
              elements: [],
              balls: [],
            }]),
    };
  }, [t]);

  // Charger entraînements
  useEffect(() => {
    const fetchTrainings = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: trainingData } = await supabase
        .from("trainings")
        .select("id, date, team_id")
        .eq("user_id", user.id)
        .order("date", { ascending: false });

      if (trainingData) setTrainings(trainingData);
    };
    fetchTrainings();
  }, []);

  // Pré-sélection via URL/state
  useEffect(() => {
    const idFromUrl = searchParams.get("training_id");
    const idFromState = location.state?.trainingId;
    const preset = idFromUrl || idFromState;
    if (preset) setSelectedTrainingId(preset);
  }, [searchParams, location.state]);

  // ✅ Auto-sélection fallback: premier item (le plus récent)
  useEffect(() => {
    if (!selectedTrainingId && trainings.length > 0) {
      setSelectedTrainingId(trainings[0].id);
    }
  }, [trainings, selectedTrainingId]);

  // Charger / réinit le plan au changement d’entraînement
  useEffect(() => {
    const loadPlan = async () => {
      if (!selectedTrainingId) {
        setPlanId(null);
        setExercises([]);
        return;
      }
      setLoadingPlan(true);
      const { data, error } = await supabase
        .from("training_plans")
        .select("id, training_id, exercises")
        .eq("training_id", selectedTrainingId)
        .maybeSingle<TrainingPlanRow>();

      if (error) {
        console.error("Erreur chargement plan :", error);
        setPlanId(null);
        setExercises([]);
      } else if (data) {
        setPlanId(data.id);
        const raw = data.exercises || [];
        const normalized = raw.map((ex, i) => normalizeBlock(ex, i));
        setExercises(normalized);
      } else {
        setPlanId(null);
        setExercises([]);
      }
      setLoadingPlan(false);
    };
    loadPlan();
  }, [selectedTrainingId, normalizeBlock]);

  /** 🔄 Charge les joueurs depuis teams.players (jsonb) */
  /** 🔄 Charge les joueurs depuis teams.players (jsonb) — plus aucun appel à public.players */
  useEffect(() => {
    const loadPlayers = async () => {
      if (!selectedTrainingId) { setTeamPlayers([]); return; }
      setPlayersLoading(true);
      try {
        // 1) retrouver l’équipe de l’entraînement sélectionné
        const training = trainings.find(t => t.id === selectedTrainingId) || null;
        const teamId = training?.team_id ?? null;

        // 2) si on a un team_id, on lit le jsonb "players" dans la table teams
        if (teamId) {
          const { data, error } = await supabase
            .from("teams")
            .select("players")
            .eq("id", teamId)
            .maybeSingle();

          if (error) {
            console.error("teams.players error:", error);
            setTeamPlayers([]);
          } else {
            const raw = Array.isArray(data?.players) ? data!.players : [];
            const mapped: PlayerLite[] = raw.map((p: any, idx: number) => ({
              firstName: p.firstName ?? p.first_name ?? p.given_name ?? "",
              lastName: p.lastName ?? p.last_name ?? p.surname ?? "",
              number: Number(p.number ?? p.jersey_number ?? p.shirt_number ?? p.no ?? idx + 1),
            }));
            setTeamPlayers(mapped);
          }
          return; // ✅ on s'arrête ici, aucun appel à "players"
        }

        // 3) fallback: on prend la dernière équipe de l’utilisateur qui a un jsonb players non nul
        const { data: auth } = await supabase.auth.getUser();
        const uid = (auth as any)?.user?.id;
        if (!uid) { setTeamPlayers([]); return; }

        const { data: anyTeam, error: anyErr } = await supabase
          .from("teams")
          .select("players")
          .eq("user_id", uid)
          .not("players", "is", null)
          .order("id", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (anyErr) {
          console.error("teams fallback error:", anyErr);
          setTeamPlayers([]);
        } else {
          const raw = Array.isArray(anyTeam?.players) ? anyTeam!.players : [];
          const mapped: PlayerLite[] = raw.map((p: any, idx: number) => ({
            firstName: p.firstName ?? p.first_name ?? p.given_name ?? "",
            lastName: p.lastName ?? p.last_name ?? p.surname ?? "",
            number: Number(p.number ?? p.jersey_number ?? p.shirt_number ?? p.no ?? idx + 1),
          }));
          setTeamPlayers(mapped);
        }
      } finally {
        setPlayersLoading(false);
      }
    };

    loadPlayers();
  }, [selectedTrainingId, trainings]);


  /* ===== Actions blocs ===== */
  const handleAddExercise = () => {
    const idx = exercises.length;
    const isFirst = idx === 0;
    setExercises((prev) => [
      ...prev,
      {
        id: uuidv4(),
        kind: isFirst ? "echauffement" : "exercice",
        name: "",
        title: isFirst ? t("planTraining:kinds.echauffement") : `${t("planTraining:kinds.exercice")} ${idx + 1}`,
        duration: "",
        objectives: [""],
        description: "",
        technicalNotes: "",
        evolutions: "",
        drawings: [{ id: uuidv4(), paths: [], type: "half" }],
      },
    ]);
  };

  const handleAddShootingBlock = () => {
    setExercises(prev => [
      ...prev,
      {
        id: uuidv4(),
        kind: "shooting",
        name: "",
        title: t("planTraining:kinds.shooting"),
        duration: "",
        objectives: [""],
        description: "",
        technicalNotes: "",
        evolutions: "",
        drawings: [],
      }
    ]);
  };


  const handleRemoveExercise = (id: string) => {
    if (!window.confirm(t("planTraining:confirm.deleteBlock"))) return;
    setExercises((prev) => prev.filter((e) => e.id !== id));
  };

  const handleUpdate = (id: string, field: keyof ExerciseBlock, value: any) => {
    setExercises((prev) => prev.map((ex) => (ex.id === id ? { ...ex, [field]: value } : ex)));
  };

  const handleUpdateObjective = (id: string, index: number, value: string) => {
    setExercises((prev) =>
      prev.map((ex) =>
        ex.id === id
          ? { ...ex, objectives: ex.objectives.map((obj, i) => (i === index ? value : obj)) }
          : ex
      )
    );
  };

  const handleRemoveDrawing = (exerciseId: string, drawingId: string) => {
    if (!window.confirm(t("planTraining:confirm.deleteDrawing"))) return;
    setExercises(prev =>
      prev.map(ex =>
        ex.id === exerciseId
          ? { ...ex, drawings: ex.drawings.filter(d => d.id !== drawingId) }
          : ex
      )
    );
  };

  const handleAddObjective = (id: string) => {
    setExercises((prev) =>
      prev.map((ex) =>
        ex.id === id && ex.objectives.length < 3 ? { ...ex, objectives: [...ex.objectives, ""] } : ex
      )
    );
  };

  const handleAddDrawing = (exerciseId: string, type: "half" | "full") => {
    setExercises(prev =>
      prev.map(ex =>
        ex.id === exerciseId
          ? { ...ex, drawings: [...ex.drawings, { id: uuidv4(), paths: [], type, elements: [], balls: [] }] }
          : ex
      )
    );
  };


  const handleUpdateDrawing = (
    exerciseId: string,
    drawingId: string,
    patch: Partial<{
      paths: { x: number; y: number }[][];
      elements: DrawElement[];
      balls: NormPoint[];
    }>
  ) => {
    setExercises(prev =>
      prev.map(ex =>
        ex.id === exerciseId
          ? {
            ...ex,
            drawings: ex.drawings.map(d =>
              d.id === drawingId ? { ...d, ...patch } : d
            ),
          }
          : ex
      )
    );
  };

  const handleSavePlan = async () => {
    if (!selectedTrainingId || exercises.length === 0) {
      alert(t("planTraining:alerts.selectTraining"));
      return;
    }
    setSaving(true);

    let error = null;
    if (planId) {
      const res = await supabase.from("training_plans").update({ exercises }).eq("id", planId);
      error = res.error ?? null;
    } else {
      const res = await supabase
        .from("training_plans")
        .insert({ training_id: selectedTrainingId, exercises })
        .select("id")
        .single();
      error = res.error ?? null;
      if (!res.error && res.data?.id) setPlanId(res.data.id);
    }

    setSaving(false);

    if (error) {
      console.error(error);
      alert(t("planTraining:alerts.saveError"));
    } else {
      alert(t("planTraining:alerts.saveSuccess"));
      navigate(`/training/${selectedTrainingId}`);
    }
  };

  const handleDeletePlan = async () => {
    if (!planId) return;
    if (!window.confirm(t("planTraining:confirm.deletePlan"))) return;
    const { error } = await supabase.from("training_plans").delete().eq("id", planId);
    if (error) {
      console.error(error);
      alert(t("planTraining:alerts.deletePlanError"));
      return;
    }
    setPlanId(null);
    setExercises([]);
    alert(t("planTraining:alerts.planDeleted"));
  };

  // 🔀 Helpers de réordonnancement
  const moveExercise = (id: string, delta: number) => {
    setExercises((prev) => {
      const index = prev.findIndex((e) => e.id === id);
      if (index === -1) return prev;
      const newIndex = index + delta;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(index, 1);
      copy.splice(newIndex, 0, item);
      return copy;
    });
  };

  const moveExerciseToIndex = (sourceId: string, targetId: string) => {
    setExercises((prev) => {
      if (sourceId === targetId) return prev;
      const src = prev.findIndex((e) => e.id === sourceId);
      const tgt = prev.findIndex((e) => e.id === targetId);
      if (src === -1 || tgt === -1) return prev;
      const next = [...prev];
      const [it] = next.splice(src, 1);
      next.splice(tgt, 0, it);
      return next;
    });
  };

  // 🔎 Dessin courant pour la popup
  const currentDrawing = useMemo(() => {
    if (!editing) return null;
    const ex = exercises.find((e) => e.id === editing.exerciseId);
    const d = ex?.drawings.find((dd) => dd.id === editing.drawingId);
    return d && ex ? { exId: ex.id, d } : null;
  }, [editing, exercises]);

  // ============== ✨ GESTION DES MODÈLES (TEMPLATES) =================
  const cloneBlockWithNewIds = (b: ExerciseBlock): ExerciseBlock => ({
    ...b,
    id: uuidv4(),
    drawings: (b.drawings ?? []).map((d) => ({ ...d, id: uuidv4(), paths: d.paths ? JSON.parse(JSON.stringify(d.paths)) : [] })),
  });

  const saveBlockAsTemplate = async (block: ExerciseBlock) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert(t("planTraining:templates.mustLogin"));
      return;
    }
    const suggested = block.name?.trim() || `${kindLabel(block.kind)} • ${block.duration || t("planTraining:defaults.noDuration")}`;
    const name = window.prompt(t("planTraining:templates.savePromptTitle"), suggested);
    if (!name) return;

    const payload: ExerciseBlock = { ...block };

    const { error } = await supabase
      .from("training_block_templates")
      .insert({
        user_id: user.id,
        name,
        kind: block.kind,
        payload,
      });

    if (error) {
      console.error(error);
      alert(t("planTraining:templates.saveError"));
      return;
    }
    alert(t("planTraining:templates.saved"));
    if (templatesOpen) await loadTemplates();
  };

  const loadTemplates = async () => {
    setTplLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setTplLoading(false); return; }

    const { data, error } = await supabase
      .from("training_block_templates")
      .select("id,user_id,name,kind,payload,created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      setTplLoading(false);
      return;
    }
    setTemplates((data || []) as BlockTemplateRow[]);
    setTplLoading(false);
  };

  const insertTemplateIntoPlan = (tpl: BlockTemplateRow) => {
    const cloned = cloneBlockWithNewIds(tpl.payload);
    setExercises((prev) => [...prev, cloned]);
    setTemplatesOpen(false);
  };

  const deleteTemplate = async (tplId: string) => {
    if (!window.confirm(t("planTraining:templates.deleteConfirm"))) return;
    const { error } = await supabase.from("training_block_templates").delete().eq("id", tplId);
    if (error) {
      console.error(error);
      alert(t("planTraining:templates.deleteError"));
      return;
    }
    setTemplates((prev) => prev.filter((t) => t.id !== tplId));
  };


  const courtHeightForWidth = (type: "half" | "full", w: number) =>
    Math.round(w * (type === "full" ? 0.5 : 1));

  const filteredTemplates = useMemo(() => {
    const q = tplQuery.trim().toLowerCase();
    return templates.filter((tRow) => {
      if (tplKind !== "all" && tRow.kind !== tplKind) return false;
      if (!q) return true;
      const hay = `${tRow.name} ${(tRow.payload?.name || "")} ${(tRow.payload?.description || "")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [templates, tplKind, tplQuery]);

  const ElementsOverlay: React.FC<{
    elements: DrawElement[];
    width: number;
    height: number;
    iconScale?: number;
    selectedId?: string;
  }> = ({ elements, width, height, iconScale = 1, selectedId }) => {
    const basePlayerR = 14;
    const r = basePlayerR * iconScale;
    const baseFont = 12;
    const font = Math.max(10, Math.round(baseFont * iconScale));

    return (
      <svg className="pointer-events-none absolute inset-0" width={width} height={height}>
        {elements.map(el => {
          const x = el.x * width;
          const y = el.y * height;
          const isSel = el.id === selectedId;

          if (el.kind === "cone") {
            const size = 18 * iconScale;
            return (
              <g key={el.id}>
                {isSel && (
                  <circle cx={x} cy={y} r={size * 1.1} fill="none" stroke="#22c55e" strokeWidth={2} opacity={0.9} />
                )}
                <polygon
                  points={`${x},${y - size} ${x - size * 0.6},${y + size * 0.6} ${x + size * 0.6},${y + size * 0.6}`}
                  fill={el.color || "#ef4444"}
                  opacity={0.9}
                />
              </g>
            );
          }

          if (el.kind === "player") {
            const hasBall = el.hasBall;
            return (
              <g key={el.id}>
                {/* Cercle ballon */}
                {hasBall && (
                  <circle
                    cx={x}
                    cy={y}
                    r={r + 6}
                    fill="none"
                    stroke={el.color || "#facc15"}   // 👈 couleur du joueur
                    strokeWidth={3}
                    opacity={0.9}
                  />
                )}

                {/* Pion joueur */}
                <circle cx={x} cy={y} r={r} fill={el.color || "#2563eb"} opacity={0.95} />
                <text
                  x={x}
                  y={y + font / 3}
                  fontSize={font}
                  fill="#ffffff"
                  textAnchor="middle"
                  fontWeight={700}
                >
                  {el.label ?? ""}
                </text>

                {/* Cercle de sélection vert */}
                {isSel && (
                  <circle
                    cx={x}
                    cy={y}
                    r={r + 10}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth={2}
                  />
                )}
              </g>
            );
          }

          if (el.kind === "text") {
            return (
              <g key={el.id}>
                <text x={x} y={y} fontSize={14} fill={el.color || "#111827"} textAnchor="middle">
                  {el.label || t("planTraining:defaults.text")}
                </text>
                {isSel && (
                  <rect
                    x={x - 24} y={y - 18} width={48} height={24}
                    fill="none" stroke="#22c55e" strokeWidth={2}
                  />
                )}
              </g>
            );
          }

          if (el.kind === "arrow" && el.x2 !== undefined && el.y2 !== undefined) {
            const x2 = el.x2 * width;
            const y2 = el.y2 * height;

            const cx = (el.cpx ?? ((el.x + el.x2) / 2)) * width;
            const cy = (el.cpy ?? ((el.y + el.y2) / 2)) * height;

            const tx = 2 * (x2 - cx);
            const ty = 2 * (y2 - cy);
            const angle = Math.atan2(ty, tx);
            const head = 9 * iconScale;
            const hx1 = x2 - head * Math.cos(angle - Math.PI / 6);
            const hy1 = y2 - head * Math.sin(angle - Math.PI / 6);
            const hx2 = x2 - head * Math.cos(angle + Math.PI / 6);
            const hy2 = y2 - head * Math.sin(angle + Math.PI / 6);

            return (
              <g key={el.id}>
                <path
                  d={`M ${x} ${y} Q ${cx} ${cy} ${x2} ${y2}`}
                  fill="none"
                  stroke={isSel ? "#22c55e" : (el.color || "#111827")}
                  strokeWidth={isSel ? 5 * iconScale : 3 * iconScale}
                />
                <polygon
                  points={`${x2},${y2} ${hx1},${hy1} ${hx2},${hy2}`}
                  fill={isSel ? "#22c55e" : (el.color || "#111827")}
                />
                {isSel && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={8 * iconScale}
                    fill="#22c55e"
                    stroke="#064e3b"
                    strokeWidth={2}
                    opacity={0.9}
                  />
                )}
              </g>
            );
          }

          if (el.kind === "pass" && el.x2 !== undefined && el.y2 !== undefined) {
            const x2 = el.x2 * width;
            const y2 = el.y2 * height;
            const head = 9 * iconScale;
            const angle = Math.atan2(y2 - y, x2 - x);
            const hx1 = x2 - head * Math.cos(angle - Math.PI / 6);
            const hy1 = y2 - head * Math.sin(angle - Math.PI / 6);
            const hx2 = x2 - head * Math.cos(angle + Math.PI / 6);
            const hy2 = y2 - head * Math.sin(angle + Math.PI / 6);

            return (
              <g key={el.id}>
                <line
                  x1={x} y1={y} x2={x2} y2={y2}
                  stroke={isSel ? "#22c55e" : (el.color || "#111827")}
                  strokeWidth={isSel ? 5 * iconScale : 3 * iconScale}
                  strokeDasharray="8 6"
                  opacity={isSel ? 0.9 : 1}
                />
                <polygon
                  points={`${x2},${y2} ${hx1},${hy1} ${hx2},${hy2}`}
                  fill={isSel ? "#22c55e" : (el.color || "#111827")}
                />
              </g>
            );
          }
          return null;
        })}
      </svg>
    );
  };

  // === Responsive pour la modale d'édition ===
  const editWrapRef = useRef<HTMLDivElement | null>(null);
  const [editSize, setEditSize] = useState<{ w: number; h: number }>({ w: 520, h: 460 });

  const getRatio = useCallback(() => {
    const ttype = currentDrawing?.d.type || "half";
    return ttype === "full" ? 0.5 : 1;
  }, [currentDrawing?.d.type]);

  const measureEditArea = useCallback(() => {
    const el = editWrapRef.current;
    if (!el) return;

    const ratio = getRatio();
    const maxWidthPx = Math.min(el.clientWidth || 0, 980);
    const top = el.getBoundingClientRect().top;
    const marginBottom = 24;
    const availableH = Math.max(240, Math.floor(window.innerHeight - top - marginBottom));
    const widthFromHeight = Math.floor(availableH / ratio);
    const w = Math.max(260, Math.min(maxWidthPx, widthFromHeight));
    const h = Math.floor(w * ratio);

    setEditSize((prev) => (prev.w !== w || prev.h !== h ? { w, h } : prev));
  }, [getRatio]);

  useLayoutEffect(() => {
    if (!editing || !currentDrawing) return;

    measureEditArea();
    const onResize = () => measureEditArea();

    let ro: ResizeObserver | undefined;
    const el = editWrapRef.current;
    const RO: typeof ResizeObserver | undefined =
      typeof window !== "undefined" ? (window as any).ResizeObserver : undefined;

    if (RO && el) {
      ro = new RO(() => measureEditArea());
      ro.observe(el);
    }

    const id1 = window.setTimeout(measureEditArea, 60);
    const id2 = window.setTimeout(measureEditArea, 160);

    window.addEventListener("resize", onResize);
    return () => {
      ro?.disconnect();
      window.clearTimeout(id1);
      window.clearTimeout(id2);
      window.removeEventListener("resize", onResize);
    };
  }, [editing, currentDrawing, measureEditArea]);

  const ElementsEditorOverlay: React.FC<{
    tool: Tool;
    color: string;
    width: number;
    height: number;
    elements: DrawElement[];
    setElements: (els: DrawElement[]) => void;
  }> = ({ tool, color, width, height, elements, setElements }) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [arrowStart, setArrowStart] = useState<{ x: number; y: number } | null>(null);
    const [mouse, setMouse] = useState<{ x?: number; y?: number }>({});
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const elsRef = useRef<DrawElement[]>(elements);
    const [curveEdit, setCurveEdit] = useState<null | { id: string }>(null);
    useEffect(() => { elsRef.current = elements; }, [elements]);

    const toNorm = (cx: number, cy: number, r: DOMRect) => ({
      x: Math.min(Math.max(0, cx - r.left), r.width) / r.width,
      y: Math.min(Math.max(0, cy - r.top), r.height) / r.height,
    });

    const d2 = (ax: number, ay: number, bx: number, by: number) => Math.hypot(ax - bx, ay - by);
    const dPointSeg = (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => {
      const vx = x2 - x1, vy = y2 - y1, wx = px - x1, wy = py - y1;
      const c1 = vx * wx + vy * wy; if (c1 <= 0) return Math.hypot(px - x1, py - y1);
      const c2 = vx * vx + vy * vy; if (c2 <= c1) return Math.hypot(px - x2, py - y2);
      const b = c1 / c2, bx = x1 + b * vx, by = y1 + b * vy; return Math.hypot(px - bx, py - by);
    };

    const nearest = (x: number, y: number): string | null => {
      let best: { id: string; d: number } | null = null;
      for (const el of elsRef.current) {
        const d = (el.kind === "arrow" && el.x2 != null && el.y2 != null)
          ? dPointSeg(x, y, el.x, el.y, el.x2, el.y2)
          : d2(x, y, el.x, el.y);
        if (d <= 0.06 && (!best || d < best.d)) best = { id: el.id, d };
      }
      return best?.id ?? null;
    };

    const nextNum = () => {
      const nums = elsRef.current
        .filter(e => e.kind === "player" && /^\d{1,2}$/.test(e.label ?? ""))
        .map(e => parseInt(e.label!, 10));
      return nums.length ? String(Math.max(...nums) + 1) : "1";
    };

    const onCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const { x, y } = toNorm(e.clientX, e.clientY, rect);

      // ✅ OUTIL BALLON : toggle hasBall joueur par joueur (multi-ballons)
      if (tool === "ball") {
        const id = nearest(x, y);
        if (!id) return;

        const base = elsRef.current;
        const target = base.find(el => el.id === id);
        if (!target || target.kind !== "player") return;

        const isCurrentlyOn = !!target.hasBall;

        const next = base.map(el => {
          if (el.id !== target.id) return el;
          // on toggle juste ce joueur, on ne touche pas aux autres
          return { ...el, hasBall: !isCurrentlyOn };
        });

        setElements(next);
        setSelectedId(target.id);
        return;
      }

      // ✅ OUTIL SÉLECTION : 2ème clic = déplacer le point de courbure d'une flèche
      if (tool === "free" && curveEdit) {
        const base = elsRef.current;
        const next = base.map(it => {
          if (it.id !== curveEdit.id) return it;
          if (it.kind !== "arrow" || it.x2 == null || it.y2 == null) return it;

          return {
            ...it,
            cpx: Math.min(Math.max(0, x), 1),
            cpy: Math.min(Math.max(0, y), 1),
          };
        });
        setElements(next);
        setCurveEdit(null);
        return;
      }

      // ✅ OUTIL SÉLECTION (classique) : sélectionner / déplacer / entrer en mode courbe
      if (tool === "free") {
        const base = elsRef.current;

        // Si un élément est sélectionné et que c'est une flèche, on regarde si on clique près du point de courbe
        if (selectedId) {
          const sel = base.find(it => it.id === selectedId);
          if (sel && sel.kind === "arrow" && sel.x2 != null && sel.y2 != null) {
            const hcpx = sel.cpx ?? (sel.x + sel.x2) / 2;
            const hcpy = sel.cpy ?? (sel.y + sel.y2) / 2;
            if (near(x, y, hcpx, hcpy)) {
              // on entre en mode édition de courbe -> prochain clic repositionne le control point
              setCurveEdit({ id: sel.id });
              return;
            }
          }
        }

        // Si rien n'est sélectionné, on essaie de sélectionner l'élément le plus proche
        if (!selectedId) {
          const id = nearest(x, y);
          setSelectedId(id);
          return;
        }

        // Sinon, on déplace l'élément sélectionné (joueur, cône, flèche...)
        const el = base.find(it => it.id === selectedId);
        if (!el) {
          setSelectedId(null);
          return;
        }

        const next = base.map(it => {
          if (it.id !== selectedId) return it;

          if (it.kind === "arrow" && it.x2 != null && it.y2 != null) {
            const dx = x - it.x;
            const dy = y - it.y;
            const nx2 = Math.min(Math.max(0, it.x2 + dx), 1);
            const ny2 = Math.min(Math.max(0, it.y2 + dy), 1);
            const ncpx = Math.min(Math.max(0, (it.cpx ?? (it.x + it.x2) / 2) + dx), 1);
            const ncpy = Math.min(Math.max(0, (it.cpy ?? (it.y + it.y2) / 2) + dy), 1);
            return { ...it, x, y, x2: nx2, y2: ny2, cpx: ncpx, cpy: ncpy };
          }

          return { ...it, x, y };
        });

        setElements(next);
        return;
      }

      // ✅ OUTIL FLÈCHE
      if (tool === "arrow") {
        if (!arrowStart) {
          setArrowStart({ x, y });
          return;
        }
        const newEl: DrawElement = {
          id: uuidv4(),
          kind: "arrow",
          x: arrowStart.x,
          y: arrowStart.y,
          x2: x,
          y2: y,
          color,
        };
        setElements([...elsRef.current, newEl]);
        setArrowStart(null);
        return;
      }

      // ✅ OUTIL TEXTE
      if (tool === "text") {
        const label = window.prompt(t("planTraining:editor.prompt.text"), "A");
        if (!label) return;
        setElements([
          ...elsRef.current,
          { id: uuidv4(), kind: "text", x, y, label, color },
        ]);
        return;
      }

      // ✅ OUTIL CÔNE
      if (tool === "cone") {
        setElements([...elsRef.current, { id: uuidv4(), kind: "cone", x, y, color }]);
        return;
      }

      // ✅ OUTIL JOUEUR
      if (tool === "player") {
        let num =
          window.prompt(t("planTraining:editor.prompt.playerNumber"), nextNum()) ||
          nextNum();
        if (!/^\d{1,2}$/.test(num)) num = nextNum();
        setElements([
          ...elsRef.current,
          { id: uuidv4(), kind: "player", x, y, label: num, color },
        ]);
        return;
      }

      // ✅ OUTIL PASSE
      if (tool === "pass") {
        if (!arrowStart) {
          setArrowStart({ x, y });
        } else {
          const newEl: DrawElement = {
            id: uuidv4(),
            kind: "pass",
            x: arrowStart.x,
            y: arrowStart.y,
            x2: x,
            y2: y,
            color,
          };
          setElements([...elsRef.current, newEl]);
          setArrowStart(null);
        }
        return;
      }
    };


    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
      if (!arrowStart) return;
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const { x, y } = toNorm(e.clientX, e.clientY, rect);
      setMouse({ x, y });
    };

    useEffect(() => {
      const onKey = (ev: KeyboardEvent) => {
        if (!selectedId) return;
        if (ev.key === "Delete" || ev.key === "Backspace") {
          ev.preventDefault();
          const next = elsRef.current.filter((el) => el.id !== selectedId);
          setElements(next);
          setSelectedId(null);
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [selectedId, setElements]);

    return (
      <div
        ref={wrapRef}
        className="absolute"
        style={{
          zIndex: 6,
          top: 0,
          left: 0,
          width,
          height,
          cursor: tool === "free" ? "pointer" : "crosshair",
          userSelect: "none",
          touchAction: "none",
          pointerEvents: tool === "pen" ? "none" : "auto",
        }}
        onClick={onCanvasClick}
        onPointerMove={onPointerMove}
        draggable={false}
      >
        {(tool === "arrow" || tool === "pass") &&
          arrowStart &&
          mouse.x != null &&
          mouse.y != null && (
            <svg className="pointer-events-none absolute inset-0" width={width} height={height}>
              <line
                x1={arrowStart.x * width}
                y1={arrowStart.y * height}
                x2={mouse.x * width}
                y2={mouse.y * height}
                stroke={color}
                strokeWidth={2}
                strokeDasharray={tool === "pass" ? "6 6" : undefined}
              />
            </svg>
          )}

        <ElementsOverlay
          elements={elements}
          width={width}
          height={height}
          selectedId={selectedId ?? undefined}
        />
      </div>
    );
  };

  return (
    <div className="app-shell max-w-4xl mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* ===== HEADER (safe notch + sticky) ===== */}
      <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur safe-header border-b border-gray-200/70 dark:border-gray-800/70">
        <div className="flex items-center gap-2">
          {/* Back button (si tu as ton composant) */}
          {/* @ts-ignore */}
          {typeof BackButton !== "undefined" ? <BackButton /> : null}

          <NotebookPen className="w-6 h-6 text-blue-600 shrink-0" />
          <h1 className="text-xl sm:text-2xl font-bold text-blue-800 dark:text-blue-200 truncate">
            {t("planTraining:title")}
          </h1>
        </div>

        {/* loading / plan id */}
        <div className="mt-2 pb-3">
          {loadingPlan && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("planTraining:loadingPlan")}
            </div>
          )}

          {!!planId && !loadingPlan && (
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              {t("planTraining:existingPlanLoadedPrefix")} •{" "}
              <span className="font-mono">#{planId.slice(0, 8)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-28">
        {/* ACTIONS (sticky sous le header, mobile-friendly) */}
        <div className="sticky top-[72px] z-30 mb-4">
          <div className="bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur rounded-xl border border-gray-200/70 dark:border-gray-800/70 p-3">
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 sm:gap-3">
              <button
                onClick={handleAddExercise}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center justify-center gap-2"
              >
                <PlusCircle className="w-5 h-5" />
                {t("planTraining:buttons.addBlock")}
              </button>

              <button
                onClick={handleAddShootingBlock}
                className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg inline-flex items-center justify-center gap-2"
              >
                <PlusCircle className="w-5 h-5" />
                {t("planTraining:buttons.addShootingBlock")}
              </button>

              <button
                onClick={async () => {
                  if (!templatesOpen) await loadTemplates();
                  setTemplatesOpen(true);
                }}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg inline-flex items-center justify-center gap-2"
              >
                <Library className="w-5 h-5" />
                {t("planTraining:buttons.insertTemplate")}
              </button>

              {planId && (
                <button
                  onClick={handleDeletePlan}
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  {t("planTraining:buttons.deletePlan")}
                </button>
              )}
            </div>
          </div>
        </div>

        {exercises.length === 0 && !loadingPlan && (
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-6">
            <AlertTriangle className="w-4 h-4" />
            {t("planTraining:noBlocks")}
          </div>
        )}

        {exercises.map((exercise, idx) => {
          const k = exercise.kind ?? (idx === 0 ? "echauffement" : "exercice");
          const isShooting = k === "shooting";

          return (
            <div
              key={exercise.id}
              onDragOver={(e) => {
                e.preventDefault();
                if (draggingId && draggingId !== exercise.id) setOverId(exercise.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const sourceId = draggingId || e.dataTransfer.getData("text/plain");
                if (sourceId) moveExerciseToIndex(sourceId, exercise.id);
                setDraggingId(null);
                setOverId(null);
              }}
              onDragEnd={() => {
                setDraggingId(null);
                setOverId(null);
              }}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.altKey && e.key === "ArrowUp") {
                  e.preventDefault();
                  moveExercise(exercise.id, -1);
                } else if (e.altKey && e.key === "ArrowDown") {
                  e.preventDefault();
                  moveExercise(exercise.id, 1);
                }
              }}
              className={[
                "bg-white dark:bg-gray-800 shadow p-4 mb-6 rounded-xl border transition",
                "border-gray-200 dark:border-gray-700",
                draggingId === exercise.id ? "opacity-60" : "",
                overId === exercise.id ? "ring-2 ring-blue-400" : "",
              ].join(" ")}
            >
              {/* En-tête bloc */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
                <div className="flex items-start gap-2 min-w-0">
                  <span
                    className="cursor-grab active:cursor-grabbing text-gray-400 dark:text-gray-500 mt-1"
                    title={t("planTraining:titles.dragToReorder")}
                  >
                    <GripVertical className="w-4 h-4" />
                  </span>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
                    {/* Sélecteur type */}
                    <select
                      value={k}
                      onChange={(e) =>
                        handleUpdate(exercise.id, "kind", e.target.value as BlockKind)
                      }
                      className="border rounded-lg px-2 py-2 bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                    >
                      <option value="echauffement">{t("planTraining:kinds.echauffement")}</option>
                      <option value="exercice">{t("planTraining:kinds.exercice")}</option>
                      <option value="prepa">{t("planTraining:kinds.prepa")}</option>
                      <option value="shooting">{t("planTraining:kinds.shooting")}</option>
                    </select>

                    {/* Nom du bloc */}
                    <input
                      value={exercise.name ?? ""}
                      onChange={(e) => handleUpdate(exercise.id, "name", e.target.value)}
                      placeholder={t("planTraining:placeholders.blockName")}
                      autoComplete="off"
                      className="border rounded-lg px-3 py-2 w-full sm:min-w-[260px] bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                    />
                  </div>
                </div>

                {/* Actions bloc (wrap sur mobile) */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => saveBlockAsTemplate(exercise)}
                    className="px-2.5 py-2 rounded-lg text-xs inline-flex items-center gap-1 border hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700 text-indigo-700 dark:text-indigo-300"
                    title={t("planTraining:titles.saveAsTemplate")}
                  >
                    <BookmarkPlus className="w-4 h-4" />
                    {t("planTraining:buttons.template")}
                  </button>

                  <button
                    onClick={() => moveExercise(exercise.id, -1)}
                    disabled={idx === 0}
                    className={`px-2.5 py-2 rounded-lg text-xs inline-flex items-center gap-1 border ${idx === 0
                      ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700"
                      : "hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700"
                      }`}
                    title={t("planTraining:titles.moveUp")}
                  >
                    <ArrowUp className="w-4 h-4" />
                    {t("planTraining:buttons.up")}
                  </button>

                  <button
                    onClick={() => moveExercise(exercise.id, 1)}
                    disabled={idx === exercises.length - 1}
                    className={`px-2.5 py-2 rounded-lg text-xs inline-flex items-center gap-1 border ${idx === exercises.length - 1
                      ? "opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700"
                      : "hover:bg-gray-100 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700"
                      }`}
                    title={t("planTraining:titles.moveDown")}
                  >
                    <ArrowDown className="w-4 h-4" />
                    {t("planTraining:buttons.down")}
                  </button>

                  <button
                    onClick={() => handleRemoveExercise(exercise.id)}
                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-900 px-2.5 py-2 rounded-lg text-xs inline-flex items-center gap-1"
                    title={t("planTraining:titles.deleteBlock")}
                  >
                    <Trash2 className="w-4 h-4" />
                    {t("planTraining:buttons.delete")}
                  </button>
                </div>
              </div>

              {/* Sous-titre */}
              <div className="mb-2 text-sm text-gray-600 dark:text-gray-300">
                <span className="font-semibold">{kindLabel(k)}</span>
                {exercise.name ? ` · ${exercise.name}` : ""}
              </div>

              {/* Corps du bloc */}
              {isShooting ? (
                <div className="mt-2">
                  {playersLoading && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Chargement des joueurs…
                    </div>
                  )}
                  {!playersLoading && teamPlayers.length === 0 && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Aucun joueur trouvé. Renseigne l’effectif dans l’équipe liée à cet entraînement.
                    </div>
                  )}
                  <ShootingBlock
                    training={{ id: selectedTrainingId }}
                    players={teamPlayers}
                    defaultTitle=""
                    onSaved={() => console.log("Bloc tir sauvegardé")}
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-1">
                      <label className="block font-medium dark:text-gray-200 leading-tight">
                        {t("planTraining:labels.duration")}
                      </label>

                      <input
                        type="text"
                        value={exercise.duration}
                        onChange={(e) => handleUpdate(exercise.id, "duration", e.target.value)}
                        className="border w-full rounded-lg bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700
               px-3 py-2 leading-none h-11"
                        inputMode="text"
                      />
                    </div>

                    <div>
                      <label className="block font-medium dark:text-gray-200">
                        {t("planTraining:labels.objectives")}
                      </label>
                      {exercise.objectives.map((obj, i) => (
                        <input
                          key={i}
                          type="text"
                          value={obj}
                          onChange={(e) => handleUpdateObjective(exercise.id, i, e.target.value)}
                          className="border p-2 w-full mb-2 rounded-lg bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                        />
                      ))}
                      {exercise.objectives.length < 3 && (
                        <button
                          onClick={() => handleAddObjective(exercise.id)}
                          className="text-blue-600 dark:text-blue-400 text-sm mt-1 hover:underline inline-flex items-center gap-1"
                        >
                          <PlusCircle className="w-4 h-4" />
                          {t("planTraining:labels.addObjective")}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block font-medium dark:text-gray-200">
                      {t("planTraining:labels.description")}
                    </label>
                    <textarea
                      rows={3}
                      value={exercise.description}
                      onChange={(e) => handleUpdate(exercise.id, "description", e.target.value)}
                      className="border p-2 w-full rounded-lg bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block font-medium dark:text-gray-200">
                      {t("planTraining:labels.technicalNotes")}
                    </label>
                    <textarea
                      rows={3}
                      value={exercise.technicalNotes}
                      onChange={(e) => handleUpdate(exercise.id, "technicalNotes", e.target.value)}
                      className="border p-2 w-full rounded-lg bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block font-medium dark:text-gray-200">
                      {t("planTraining:labels.evolutions")}
                    </label>
                    <textarea
                      rows={3}
                      value={exercise.evolutions}
                      onChange={(e) => handleUpdate(exercise.id, "evolutions", e.target.value)}
                      className="border p-2 w-full rounded-lg bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                    />
                  </div>

                  <div className="mb-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => handleAddDrawing(exercise.id, "half")}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-300 dark:hover:bg-blue-950/30 w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4" /> {t("planTraining:buttons.addHalfCourt")}
                    </button>
                    <button
                      onClick={() => handleAddDrawing(exercise.id, "full")}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-900 dark:text-indigo-300 dark:hover:bg-indigo-950/30 w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4" /> {t("planTraining:buttons.addFullCourt")}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {exercise.drawings.map((drawing) => (
                      <div key={drawing.id} className="relative group">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDrawing(exercise.id, drawing.id);
                          }}
                          className="absolute top-2 left-2 z-10 px-2 py-1 rounded text-xs inline-flex items-center gap-1
                          bg-red-600 text-white hover:bg-red-700 shadow
                          opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition"
                          title={t("planTraining:titles.deleteThisCourt")}
                          aria-label={t("planTraining:titles.deleteThisCourt")}
                        >
                          <Trash2 className="w-3 h-3" />
                          {t("planTraining:buttons.deleteShort")}
                        </button>

                        <div
                          role="button"
                          onClick={() => setEditing({ exerciseId: exercise.id, drawingId: drawing.id })}
                          title={t("planTraining:titles.editLarge")}
                          className="inline-block w-full relative"
                        >
                          <div className="relative w-full">
                            {(() => {
                              const tW = 295;
                              const tH = courtHeightForWidth(drawing.type, tW);

                              return (
                                <div className="mx-auto">
                                  <div className="relative" style={{ width: tW, height: tH }}>
                                    <TrainingDrawingCanvas
                                      onSave={(paths, meta) =>
                                        handleUpdateDrawing(exercise.id, drawing.id, {
                                          paths,
                                          ...(meta?.balls ? { balls: meta.balls } : {}),
                                        })
                                      }
                                      initialPaths={drawing.paths}
                                      initialBallsNormalized={drawing.balls}
                                      type={drawing.type}
                                      readOnly
                                      targetWidth={tW}
                                      targetHeightCss={tH}   // ✅ important : verrouille la hauteur en preview
                                      showClearButton={false}
                                    />

                                    {/* ✅ overlay calé EXACTEMENT sur le terrain */}
                                    <ElementsOverlay
                                      elements={drawing.elements || []}
                                      width={tW}
                                      height={tH}
                                      iconScale={tW / 520}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="absolute top-2 right-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                          <span className="px-2 py-1 text-xs bg-black/60 text-white rounded inline-flex items-center gap-1">
                            <Maximize2 className="w-3 h-3" /> {t("planTraining:badges.enlarge")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* spacer bottom for fixed save bar */}
        <div className="h-4" />
      </div>

      {/* ===== SAVE BAR (fixed bottom + safe) ===== */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200/70 dark:border-gray-800/70 safe-footer">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={handleSavePlan}
            disabled={saving || !selectedTrainingId || exercises.length === 0}
            className={`w-full px-6 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 text-white ${saving || !selectedTrainingId || exercises.length === 0
              ? "bg-green-600/60 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
              }`}
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {planId ? t("planTraining:save.update") : t("planTraining:save.create")}
          </button>
        </div>
      </div>

      {/* 🪟 Popup d’édition de dessin */}
      {editing && currentDrawing && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(null)} />

          <div className="relative mx-auto mt-0 sm:mt-6 w-[min(100vw,1100px)] rounded-none sm:rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl safe-header safe-footer">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur">
              <h3 className="font-semibold dark:text-gray-100">{t("planTraining:editor.title")}</h3>
              <button
                onClick={() => setEditing(null)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t("planTraining:buttons.close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* TOOLBAR */}
            <div className="px-4 pt-3">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-sm text-gray-600 dark:text-gray-300 mr-2">
                  {t("planTraining:editor.toolbar.toolLabel")}
                </span>

                {([
                  ["free", t("planTraining:editor.tools.free")],
                  ["arrow", t("planTraining:editor.tools.arrow")],
                  ["pass", t("planTraining:editor.tools.pass")],
                  ["pen", t("planTraining:editor.tools.pen")],
                  ["cone", t("planTraining:editor.tools.cone")],
                  ["player", t("planTraining:editor.tools.player")],
                  ["ball", "Ballon"],
                  ["text", t("planTraining:editor.tools.text")],
                ] as [Tool, string][]).map(([kk, lbl]) => (
                  <button
                    key={kk}
                    onClick={() => setTool(kk)}
                    className={`px-2.5 py-2 rounded-lg border text-sm ${tool === kk
                      ? "bg-blue-600 text-white border-blue-700"
                      : "border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200"
                      }`}
                  >
                    {lbl}
                  </button>
                ))}

                <div className="flex items-center gap-2 ml-0 sm:ml-3">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {t("planTraining:editor.toolbar.colorLabel")}
                  </span>
                  <input
                    type="color"
                    value={toolColor}
                    onChange={(e) => setToolColor(e.target.value)}
                    className="w-10 h-10 p-0 border rounded-lg"
                    title={t("planTraining:editor.toolbar.colorLabel")}
                  />
                </div>

                <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => {
                      if (!window.confirm(t("planTraining:editor.confirm.clearStrokes"))) return;
                      handleUpdateDrawing(currentDrawing.exId, currentDrawing.d.id, { paths: [] });
                    }}
                    className="px-2.5 py-2 rounded-lg border text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    title={t("planTraining:editor.toolbar.clearStrokes")}
                  >
                    {t("planTraining:editor.toolbar.clearStrokes")}
                  </button>

                  <button
                    onClick={() => {
                      const els = (currentDrawing.d.elements || []).length;
                      if (!els || !window.confirm(t("planTraining:editor.confirm.clearElements"))) return;
                      handleUpdateDrawing(currentDrawing.exId, currentDrawing.d.id, { elements: [] });
                    }}
                    className="px-2.5 py-2 rounded-lg border text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    title={t("planTraining:editor.toolbar.clearElements")}
                  >
                    {t("planTraining:editor.toolbar.clearElements")}
                  </button>

                  <button
                    onClick={() => {
                      if (!window.confirm(t("planTraining:editor.confirm.clearAll"))) return;
                      handleUpdateDrawing(currentDrawing.exId, currentDrawing.d.id, { paths: [], elements: [] });
                    }}
                    className="px-2.5 py-2 rounded-lg border text-sm text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title={t("planTraining:editor.toolbar.clearAll")}
                  >
                    {t("planTraining:editor.toolbar.clearAll")}
                  </button>
                </div>
              </div>
            </div>

            {/* ZONE SCROLLABLE + RESPONSIVE */}
            <div className="px-4 pb-4 max-h-[85vh] overflow-auto">
              <div ref={editWrapRef}>
                <div
                  className="relative mx-auto"
                  style={{ width: editSize.w, height: editSize.h }}
                  draggable={false}
                >
                  {(() => {
                    const w = editSize.w;
                    return (
                      <TrainingDrawingCanvas
                        key={currentDrawing.d.id}
                        onSave={(incomingPaths, meta) => {
                          handleUpdateDrawing(currentDrawing.exId, currentDrawing.d.id, {
                            paths: incomingPaths,
                            ...(meta?.balls ? { balls: meta.balls } : {}),
                          });
                        }}
                        initialPaths={currentDrawing.d.paths}
                        initialBallsNormalized={currentDrawing.d.balls}
                        type={currentDrawing.d.type}
                        targetWidth={w}
                      />
                    );
                  })()}

                  <ElementsOverlay
                    elements={currentDrawing.d.elements || []}
                    width={editSize.w}
                    height={editSize.h}
                  />

                  <ElementsEditorOverlay
                    tool={tool}
                    color={toolColor}
                    width={editSize.w}
                    height={editSize.h}
                    elements={currentDrawing.d.elements || []}
                    setElements={(els) =>
                      handleUpdateDrawing(currentDrawing.exId, currentDrawing.d.id, { elements: els })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🪟 Bibliothèque de modèles */}
      {templatesOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setTemplatesOpen(false)} />

          <div className="relative max-w-4xl mx-auto mt-0 sm:mt-10 bg-white dark:bg-gray-900 rounded-none sm:rounded-lg shadow-xl p-4 border border-gray-200 dark:border-gray-800 safe-header safe-footer">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Library className="w-5 h-5" />
                <h3 className="font-semibold dark:text-gray-100">
                  {t("planTraining:templates.header")}
                </h3>
              </div>
              <button
                onClick={() => setTemplatesOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t("planTraining:buttons.close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
                <input
                  value={tplQuery}
                  onChange={(e) => setTplQuery(e.target.value)}
                  placeholder={t("planTraining:templates.searchPlaceholder")}
                  className="pl-8 pr-3 py-2 w-full rounded-lg border bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <select
                  value={tplKind}
                  onChange={(e) => setTplKind(e.target.value as any)}
                  className="border rounded-lg px-2 py-2 bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 w-full sm:w-auto"
                >
                  <option value="all">{t("planTraining:templates.filter.all")}</option>
                  <option value="echauffement">{t("planTraining:templates.filter.echauffement")}</option>
                  <option value="exercice">{t("planTraining:templates.filter.exercice")}</option>
                  <option value="prepa">{t("planTraining:templates.filter.prepa")}</option>
                </select>
              </div>
            </div>

            {tplLoading ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("planTraining:templates.loading")}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-gray-600 dark:text-gray-300">{t("planTraining:templates.none")}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-auto pr-1">
                {filteredTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="border rounded-xl p-3 dark:border-gray-700 bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{tpl.name}</div>
                        <div className="text-xs text-gray-500">
                          {kindLabel(tpl.kind)} •{" "}
                          {new Date(tpl.created_at).toLocaleString(i18n.language || "fr-FR")}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteTemplate(tpl.id)}
                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-900 px-2 py-2 rounded-lg text-xs inline-flex items-center gap-1 shrink-0"
                        title={t("planTraining:templates.delete")}
                      >
                        <Trash className="w-3 h-3" />
                        {t("planTraining:buttons.deleteShort")}
                      </button>
                    </div>

                    {tpl.payload?.drawings?.[0] ? (
                      (() => {
                        const first = normalizeLegacyDrawing(tpl.payload.drawings[0]);
                        const tW = 280;
                        const tH = courtHeightForWidth(first.type, tW);

                        return (
                          <div className="mb-3">
                            <div className="relative mx-auto" style={{ width: tW, height: tH }}>
                              <TrainingDrawingCanvas
                                initialPaths={first.paths}
                                type={first.type}
                                readOnly
                                targetWidth={tW}
                                onSave={() => { }}
                              />
                              <div className="absolute inset-0">
                                <ElementsOverlay
                                  elements={first.elements || []}
                                  width={tW}
                                  height={tH}
                                  iconScale={tW / 520}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-xs text-gray-500 mb-3">{t("planTraining:templates.noDrawing")}</div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={() => insertTemplateIntoPlan(tpl)}
                        className="px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm inline-flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        {t("planTraining:templates.insert")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

};
